import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { prop } from 'ramda'
import { LanguageUtils, NumberUtils, Utils } from '@pbt/pbt-ui-components'

import { getCurrentBusiness } from '../../../../store/duck/businesses'
import {
  getAppointmentTypes,
  getWellnessPlanLimitType,
} from '../../../../store/duck/constants'
import { getBenefitIsParasitePrevention } from '../wellnessPlanUtils'

const useStyles = makeStyles(
  (theme) => ({
    tableCell: {
      padding: theme.spacing(1.5, 1),
    },
    borderCell: {
      borderRight: theme.constants.tableBorder,
    },
    limitsCell: {
      minWidth: 125,
    },
    priceText: {
      fontSize: '1.4rem',
    },
    descriptionCell: {
      padding: theme.spacing(0, 1, 1),
    },
    discountDescriptionText: {
      color: theme.colors.primaryText,
    },
    limitsText: {
      color: prop('buttonsColor'),
    },
    benefitNameText: {
      color: theme.colors.primaryText,
      fontWeight: 500,
      fontSize: '1.4rem',
    },
    benefitContainer: {
      minHeight: 'fit-content',
      flex: 1,
      '&:not(:last-of-type)': {
        borderBottom: theme.constants.tableBorder,
      },
    },
    coverRow: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.colors.tableOddRowBackground,
      },
      padding: theme.spacing(1, 2),
    },
    coverNameText: {
      minWidth: 0,
    },
  }),
  { name: 'PlanDetailsBenefitRow' },
)

const getCoverName = (cover) =>
  cover.displayName || LanguageUtils.getTranslatedFieldName(cover) || cover.name

const PlanDetailsBenefitRow = ({ benefit }) => {
  const { t } = useTranslation('Common')
  const WellnessPlanLimitType = useSelector(getWellnessPlanLimitType)
  const AppointmentTypes = useSelector(getAppointmentTypes)
  const business = useSelector(getCurrentBusiness)

  const { customColorsEnabled, buttonsColor } = business || {}

  const classes = useStyles({
    buttonsColor: customColorsEnabled ? buttonsColor : undefined,
  })

  const LimitedTypeId = Utils.findConstantIdByName(
    'Limited',
    WellnessPlanLimitType,
  )

  const isLimited = benefit.limitTypeId === LimitedTypeId
  const shouldShowDiscount =
    getBenefitIsParasitePrevention(benefit) && Boolean(benefit.discountApplied)

  const getCoverDiscountLabel = (cover) => {
    if (!shouldShowDiscount || cover.coverPercent == null) {
      return null
    }

    const discountLabel = t('DISCOUNT').toLowerCase()

    return `${NumberUtils.toPercentFormat(
      cover.coverPercent,
      2,
    )} ${discountLabel}`
  }

  return (
    <Grid
      container
      item
      xs
      className={classes.benefitContainer}
      key={benefit.id}
      wrap="nowrap"
    >
      <Grid container item className={classes.borderCell} xs={8}>
        <Grid container item wrap="nowrap">
          <Grid
            container
            item
            xs
            className={classes.tableCell}
            direction="column"
          >
            <Typography className={classes.benefitNameText}>
              {benefit.name}
            </Typography>
            {shouldShowDiscount && (
              <Typography className={classes.discountDescriptionText}>
                {t('APPLY_DISCOUNT')}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            className={classNames(classes.tableCell, classes.limitsCell)}
          >
            {benefit.limitTypeId && (
              <Typography
                className={classNames(classes.priceText, classes.limitsText)}
                variant="body2"
              >
                {LanguageUtils.getConstantTranslatedName(
                  benefit.limitTypeId,
                  WellnessPlanLimitType,
                )}
                {isLimited ? ` ${benefit.limit}/year` : ''}
              </Typography>
            )}
          </Grid>
        </Grid>
        {benefit.description && (
          <Grid
            item
            className={classNames(classes.tableCell, classes.descriptionCell)}
            xs={12}
          >
            <Typography>{benefit.description}</Typography>
          </Grid>
        )}
      </Grid>
      <Grid container item xs direction="column" wrap="nowrap">
        {benefit.appointmentTypeIds?.map((appointmentTypeId) => {
          const name = Utils.findById(appointmentTypeId, AppointmentTypes)?.name

          return (
            <Grid item className={classes.coverRow} key={appointmentTypeId}>
              <Typography className={classes.priceText} variant="body2">
                {name}
              </Typography>
            </Grid>
          )
        })}
        {benefit.cover?.map((cover) => {
          const coverDiscountLabel = getCoverDiscountLabel(cover)

          return (
            <Grid
              container
              item
              className={classes.coverRow}
              columnGap={1}
              key={cover.id}
              wrap="nowrap"
            >
              <Grid item xs className={classes.coverNameText}>
                <Typography className={classes.priceText} variant="body2">
                  {getCoverName(cover)}
                </Typography>
              </Grid>
              {coverDiscountLabel && (
                <Grid item>
                  <Typography className={classes.priceText} variant="body2">
                    {coverDiscountLabel}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )
        })}
      </Grid>
    </Grid>
  )
}

export default PlanDetailsBenefitRow
